import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  EmojiEvents as TrophyIcon,
  Timeline as TimelineIcon,
  School as SchoolIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const MaPerformance = () => {
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    chargerDonnees();
  }, []);

  const chargerDonnees = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('fhc_token');
      
      if (!token) {
        setError('Vous devez être connecté pour voir vos performances');
        setLoading(false);
        return;
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://plateforme.fhcgroupesarl.com/api';
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      // Charger la performance actuelle
      const resPerf = await fetch(`${API_BASE_URL}/performance/ma-performance`, {
        method: 'GET',
        headers: headers
      });

      if (!resPerf.ok) {
        if (resPerf.status === 401) {
          setError('Session expirée. Veuillez vous reconnecter.');
          localStorage.removeItem('fhc_token');
          localStorage.removeItem('fhc_user');
          setTimeout(() => {
            window.location.href = '/auth';
          }, 2000);
          return;
        }
        throw new Error(`Erreur ${resPerf.status}`);
      }

      const perfData = await resPerf.json();
      setPerformance(perfData);

      // Charger l'historique (6 derniers mois)
      const resHisto = await fetch(`${API_BASE_URL}/performance/mon-historique?nombre_mois=6`, {
        method: 'GET',
        headers: headers
      });

      if (resHisto.ok) {
        const histoData = await resHisto.json();
        setHistorique(histoData);
      }

    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getLabelColor = (label) => {
    const colors = {
      'Excellent': 'success',
      'Très Bien': 'info',
      'Bien': 'primary',
      'Satisfaisant': 'warning',
      'Passable': 'warning',
      'Insuffisant': 'error',
    };
    return colors[label] || 'default';
  };

  const getProgressColor = (score) => {
    if (score >= 90) return 'success';
    if (score >= 80) return 'info';
    if (score >= 70) return 'primary';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getTendanceIcon = (tendance) => {
    switch (tendance) {
      case 'croissante':
        return <TrendingUpIcon sx={{ color: 'success.main' }} />;
      case 'décroissante':
        return <TrendingDownIcon sx={{ color: 'error.main' }} />;
      default:
        return <TrendingFlatIcon sx={{ color: 'info.main' }} />;
    }
  };

  const getTendanceText = (tendance) => {
    switch (tendance) {
      case 'croissante':
        return 'En progression 📈';
      case 'décroissante':
        return 'En baisse 📉';
      default:
        return 'Stable ➡️';
    }
  };

  const getClassementEmoji = (position, total) => {
    const pourcentage = (position / total) * 100;
    if (position === 1) return '🥇';
    if (position === 2) return '🥈';
    if (position === 3) return '🥉';
    if (pourcentage <= 10) return '🌟';
    if (pourcentage <= 25) return '⭐';
    if (pourcentage <= 50) return '👍';
    return '💪';
  };

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    if (!historique?.historique || historique.historique.length === 0) {
      return null;
    }

    const labels = historique.historique.map(h => h.mois_label || h.mois);
    const data = historique.historique.map(h => h.score_global);

    return {
      labels,
      datasets: [
        {
          label: 'Score Global',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Évolution de votre performance',
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      },
    },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} display="flex" flexDirection="column" alignItems="center" gap={2}>
        <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
          <Typography variant="body2" fontWeight="bold">Impossible de charger les données de performance</Typography>
          <Typography variant="caption">{error}</Typography>
        </Alert>
        <button
          onClick={chargerDonnees}
          style={{ background: '#ab47bc', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          🔄 Réessayer
        </button>
      </Box>
    );
  }

  if (!performance) {
    return (
      <Box p={3}>
        <Alert severity="info">Aucune donnée de performance disponible</Alert>
      </Box>
    );
  }

  const { employee, performance: perf, classement, comparaison } = performance;
  const chartData = prepareChartData();

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', p: 3 }}>
      {/* En-tête avec score global - Design amélioré */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Bouton Actualiser en haut à droite */}
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
          <Tooltip title="Actualiser les données">
            <IconButton
              onClick={chargerDonnees}
              disabled={loading}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                },
                '&:disabled': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.5)',
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Avatar et Info */}
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar 
                  sx={{ 
                    width: 90, 
                    height: 90, 
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: '2.5rem',
                    border: '4px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                  }}
                >
                  {employee.nom.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                    {employee.nom}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5 }}>
                    {employee.poste}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {employee.departement} • {employee.filiale}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Score Global - Grand affichage */}
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Score Global
                </Typography>
                <Typography variant="h1" sx={{ color: 'white', fontWeight: 800, fontSize: '4rem', lineHeight: 1, mb: 1 }}>
                  {perf.score_global}%
                </Typography>
                <Chip
                  label={perf.score_label}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    height: 36,
                    backdropFilter: 'blur(10px)'
                  }}
                  size="large"
                />
              </Box>
            </Grid>

            {/* Classement */}
            <Grid item xs={12} md={4}>
              <Box textAlign="center">
                <Typography variant="h2" sx={{ fontSize: '3.5rem', mb: 1 }}>
                  {getClassementEmoji(classement.position, classement.total)}
                </Typography>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                  #{classement.position} <Typography component="span" variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>/ {classement.total}</Typography>
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Top {100 - classement.pourcentage_superieur}% de l'entreprise
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Comparaison avec la moyenne */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Votre Score
              </Typography>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 700, my: 1 }}>
                {comparaison.mon_score}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ borderRadius: 2, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Moyenne Entreprise
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: '#757575' }}>
                {comparaison.moyenne_entreprise}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ borderRadius: 2, bgcolor: comparaison.difference >= 0 ? '#e8f5e9' : '#ffebee', border: '1px solid ' + (comparaison.difference >= 0 ? '#4caf50' : '#f44336') }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: comparaison.difference >= 0 ? '#2e7d32' : '#c62828' }}>
                Différence
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, my: 1, color: comparaison.difference >= 0 ? '#2e7d32' : '#c62828' }}>
                {comparaison.difference >= 0 ? '+' : ''}{comparaison.difference}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Détails des Critères - Nouvelle présentation en grille */}
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#1a237e' }}>
        📊 Détails de vos Critères de Performance
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Object.entries(perf.details).map(([key, detail]) => {
          const config = {
            presence: { 
              icon: '🎯', 
              title: 'Présence & Ponctualité',
              color: '#2196f3',
              gradient: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
            },
            taches: { 
              icon: '⏰', 
              title: 'Exécution des Tâches',
              color: '#ff9800',
              gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
            },
            delai_taches: { 
              icon: '⏰', 
              title: 'Délai d\'Exécution',
              color: '#ff9800',
              gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)'
            },
            objectifs: { 
              icon: '🏆', 
              title: 'Atteinte des Objectifs',
              color: '#4caf50',
              gradient: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
            },
            travail_equipe: { 
              icon: '🤝', 
              title: 'Travail en Équipe',
              color: '#9c27b0',
              gradient: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)'
            },
            respect_regles: { 
              icon: '📋', 
              title: 'Respect des Règles',
              color: '#00bcd4',
              gradient: 'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)'
            },
          };

          const cfg = config[key] || config.taches; // Fallback vers taches si la clé n'existe pas

          return (
            <Grid item xs={12} md={6} lg={4} key={key}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* En-tête du critère */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box 
                        sx={{ 
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          background: cfg.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem'
                        }}
                      >
                        {cfg.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#424242', lineHeight: 1.2 }}>
                          {cfg.title}
                        </Typography>
                        <Chip 
                          label={`Poids: ${detail.poids}%`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Score circulaire */}
                  <Box position="relative" display="inline-flex" sx={{ width: '100%', justifyContent: 'center', mb: 2 }}>
                    <Box position="relative">
                      <Box
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          background: `conic-gradient(${cfg.color} ${detail.score * 3.6}deg, #f5f5f5 0deg)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}
                      >
                        <Box
                          sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            bgcolor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column'
                          }}
                        >
                          <Typography variant="h4" sx={{ fontWeight: 800, color: cfg.color }}>
                            {detail.score}%
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Score
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Contribution */}
                  <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1.5, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Contribution au score global
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: cfg.color }}>
                      +{detail.contribution}%
                    </Typography>
                  </Box>

                  {/* Détails du calcul - Accordéon */}
                  {detail.calcul && (
                    <Box sx={{ bgcolor: '#fafafa', borderRadius: 2, p: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        📊 Détails du calcul
                      </Typography>
                      
                      {/* Présence */}
                      {key === 'presence' && detail.calcul.total_jours > 0 && (
                        <Box>
                          <Box sx={{ fontSize: '0.75rem', color: '#757575' }}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <span>✓ Présents</span>
                              <strong>{detail.calcul.presents}/{detail.calcul.total_jours}</strong>
                            </Box>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <span>⚠ Retards</span>
                              <strong>{detail.calcul.retards}</strong>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <span>✗ Absences</span>
                              <strong>{detail.calcul.absents}</strong>
                            </Box>
                          </Box>

                          {/* Affichage par mois si disponible */}
                          {detail.calcul.par_mois && detail.calcul.par_mois.length > 0 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📅 Performance par mois:
                              </Typography>
                              {detail.calcul.par_mois.map((mois, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    bgcolor: 'white', 
                                    borderRadius: 1, 
                                    p: 1, 
                                    mb: 1,
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                      {mois.mois_label}
                                    </Typography>
                                    <Chip 
                                      label={`${mois.score_mois}%`}
                                      size="small"
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        bgcolor: mois.score_mois >= 80 ? '#4caf50' : mois.score_mois >= 60 ? '#ff9800' : '#f44336',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9e9e9e' }}>
                                    {mois.presents} présents • {mois.retards} retards • {mois.absents} absents
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Affichage par semaine si disponible */}
                          {detail.calcul.par_semaine && detail.calcul.par_semaine.length > 0 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📅 Performance par semaine:
                              </Typography>
                              {detail.calcul.par_semaine.map((semaine, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    bgcolor: 'white', 
                                    borderRadius: 1, 
                                    p: 1, 
                                    mb: 1,
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                      {semaine.semaine_label}
                                    </Typography>
                                    <Chip 
                                      label={`${semaine.score_semaine}%`}
                                      size="small"
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        bgcolor: semaine.score_semaine >= 80 ? '#4caf50' : semaine.score_semaine >= 60 ? '#ff9800' : '#f44336',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9e9e9e' }}>
                                    {semaine.presents} présents • {semaine.retards} retards • {semaine.absents} absents
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Affichage par jour si disponible */}
                          {detail.calcul.par_jour && detail.calcul.par_jour.length > 0 && detail.calcul.par_jour.length <= 7 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📆 Détail par jour:
                              </Typography>
                              {detail.calcul.par_jour.map((jour, idx) => (
                                <Box 
                                  key={idx}
                                  display="flex" 
                                  justifyContent="space-between" 
                                  alignItems="center"
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    py: 0.5,
                                    borderBottom: idx < detail.calcul.par_jour.length - 1 ? '1px solid #f5f5f5' : 'none'
                                  }}
                                >
                                  <span style={{ color: '#757575' }}>
                                    {new Date(jour.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                                  </span>
                                  <span style={{ 
                                    color: jour.statut === 'ok' ? '#4caf50' : jour.statut === 'retard' ? '#ff9800' : '#f44336',
                                    fontWeight: 600
                                  }}>
                                    {jour.label}
                                  </span>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Tâches (nouveau format avec complétion + délais) */}
                      {(key === 'taches' || key === 'delai_taches') && detail.calcul && detail.calcul.total_taches > 0 && (
                        <Box>
                          <Box sx={{ fontSize: '0.75rem', color: '#757575', mb: 2 }}>
                            {/* Taux de complétion */}
                            {detail.calcul.completees !== undefined && (
                              <>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <span>📋 Total tâches</span>
                                  <strong>{detail.calcul.total_taches}</strong>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <span>✅ Complétées</span>
                                  <strong>{detail.calcul.completees}</strong>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <span>⏳ En cours</span>
                                  <strong>{detail.calcul.en_cours}</strong>
                                </Box>
                                {detail.calcul.taux_completion !== undefined && (
                                  <Box display="flex" justifyContent="space-between" mb={1} sx={{ pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                    <span style={{ fontWeight: 600 }}>📊 Taux complétion</span>
                                    <strong style={{ color: '#2196f3' }}>{detail.calcul.taux_completion}%</strong>
                                  </Box>
                                )}
                              </>
                            )}
                            
                            {/* Respect des délais */}
                            {detail.calcul.taches_avec_deadline !== undefined && detail.calcul.taches_avec_deadline > 0 && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <span>⏰ Tâches avec deadline</span>
                                  <strong>{detail.calcul.taches_avec_deadline}</strong>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <span>✓ À temps</span>
                                  <strong>{detail.calcul.a_temps || 0}</strong>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <span>⚠ En retard</span>
                                  <strong>{detail.calcul.en_retard || 0}</strong>
                                </Box>
                                {detail.calcul.score_delais !== undefined && (
                                  <Box display="flex" justifyContent="space-between" mt={1} sx={{ pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                    <span style={{ fontWeight: 600 }}>⏱️ Score délais</span>
                                    <strong style={{ color: '#ff9800' }}>{detail.calcul.score_delais}%</strong>
                                  </Box>
                                )}
                              </>
                            )}

                            {/* Ancien format (compatibilité) */}
                            {detail.calcul.completees === undefined && detail.calcul.a_temps !== undefined && (
                              <>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                  <span>✓ À temps</span>
                                  <strong>{detail.calcul.a_temps}/{detail.calcul.total_taches}</strong>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <span>⚠ En retard</span>
                                  <strong>{detail.calcul.en_retard}</strong>
                                </Box>
                              </>
                            )}
                          </Box>

                          {/* Par mois */}
                          {detail.calcul.par_mois && detail.calcul.par_mois.length > 0 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📅 Performance par mois:
                              </Typography>
                              {detail.calcul.par_mois.map((mois, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    bgcolor: 'white', 
                                    borderRadius: 1, 
                                    p: 1, 
                                    mb: 1,
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                      {mois.mois_label}
                                    </Typography>
                                    <Chip 
                                      label={`${mois.score_mois || mois.score_jour || 0}%`}
                                      size="small"
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        bgcolor: (mois.score_mois || mois.score_jour || 0) >= 80 ? '#4caf50' : (mois.score_mois || mois.score_jour || 0) >= 60 ? '#ff9800' : '#f44336',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9e9e9e' }}>
                                    {mois.completees !== undefined ? (
                                      `${mois.completees}/${mois.total_taches} complétées (${mois.taux_completion || 0}%)`
                                    ) : (
                                      `${mois.a_temps || 0} à temps • ${mois.en_retard || 0} en retard • ${mois.total_taches} tâches`
                                    )}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Par semaine */}
                          {detail.calcul.par_semaine && detail.calcul.par_semaine.length > 0 && detail.calcul.par_semaine.length <= 4 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📅 Performance par semaine:
                              </Typography>
                              {detail.calcul.par_semaine.map((semaine, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    bgcolor: 'white', 
                                    borderRadius: 1, 
                                    p: 1, 
                                    mb: 1,
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                      {semaine.semaine_label}
                                    </Typography>
                                    <Chip 
                                      label={`${semaine.score_semaine || 0}%`}
                                      size="small"
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        bgcolor: (semaine.score_semaine || 0) >= 80 ? '#4caf50' : (semaine.score_semaine || 0) >= 60 ? '#ff9800' : '#f44336',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9e9e9e' }}>
                                    {semaine.completees !== undefined ? (
                                      `${semaine.completees}/${semaine.total_taches} complétées (${semaine.taux_completion || 0}%)`
                                    ) : (
                                      `${semaine.a_temps || 0} à temps • ${semaine.en_retard || 0} en retard`
                                    )}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Objectifs */}
                      {key === 'objectifs' && detail.calcul.total_objectifs > 0 && (
                        <Box>
                          <Box sx={{ fontSize: '0.75rem', color: '#757575', mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <span>✓ Atteints</span>
                              <strong>{detail.calcul.atteints}/{detail.calcul.total_objectifs}</strong>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <span>📈 En cours</span>
                              <strong>{detail.calcul.en_cours} ({detail.calcul.progression_moyenne}%)</strong>
                            </Box>
                          </Box>

                          {/* Par mois */}
                          {detail.calcul.par_mois && detail.calcul.par_mois.length > 0 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📅 Performance par mois:
                              </Typography>
                              {detail.calcul.par_mois.map((mois, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    bgcolor: 'white', 
                                    borderRadius: 1, 
                                    p: 1, 
                                    mb: 1,
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                      {mois.mois_label}
                                    </Typography>
                                    <Chip 
                                      label={`${mois.score_mois}%`}
                                      size="small"
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        bgcolor: mois.score_mois >= 80 ? '#4caf50' : mois.score_mois >= 60 ? '#ff9800' : '#f44336',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9e9e9e' }}>
                                    {mois.atteints} atteints • {mois.en_cours} en cours • Prog: {mois.progression_moyenne}%
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Par semaine */}
                          {detail.calcul.par_semaine && detail.calcul.par_semaine.length > 0 && detail.calcul.par_semaine.length <= 4 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📅 Performance par semaine:
                              </Typography>
                              {detail.calcul.par_semaine.map((semaine, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    bgcolor: 'white', 
                                    borderRadius: 1, 
                                    p: 1, 
                                    mb: 1,
                                    border: '1px solid #e0e0e0'
                                  }}
                                >
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                                      {semaine.semaine_label}
                                    </Typography>
                                    <Chip 
                                      label={`${semaine.score_semaine}%`}
                                      size="small"
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '0.65rem',
                                        bgcolor: semaine.score_semaine >= 80 ? '#4caf50' : semaine.score_semaine >= 60 ? '#ff9800' : '#f44336',
                                        color: 'white'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#9e9e9e' }}>
                                    {semaine.atteints} atteints • {semaine.en_cours} en cours
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Par jour (si période courte) */}
                          {detail.calcul.par_jour && detail.calcul.par_jour.length > 0 && detail.calcul.par_jour.length <= 7 && (
                            <Box mt={2}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#616161', display: 'block', mb: 1 }}>
                                📆 Détail par jour:
                              </Typography>
                              {detail.calcul.par_jour.map((jour, idx) => (
                                <Box 
                                  key={idx}
                                  display="flex" 
                                  justifyContent="space-between" 
                                  alignItems="center"
                                  sx={{ 
                                    fontSize: '0.7rem', 
                                    py: 0.5,
                                    borderBottom: idx < detail.calcul.par_jour.length - 1 ? '1px solid #f5f5f5' : 'none'
                                  }}
                                >
                                  <span style={{ color: '#757575' }}>
                                    {new Date(jour.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                                  </span>
                                  <span style={{ 
                                    color: jour.score_jour >= 80 ? '#4caf50' : jour.score_jour >= 60 ? '#ff9800' : '#f44336',
                                    fontWeight: 600,
                                    fontSize: '0.65rem'
                                  }}>
                                    {jour.total_objectifs} obj. ({jour.score_jour}%)
                                  </span>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Note manuelle */}
                      {(key === 'travail_equipe' || key === 'respect_regles') && (
                        <Typography variant="caption" sx={{ color: '#757575', fontStyle: 'italic' }}>
                          Note saisie par le DG
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {/* Graphique d'évolution - Design amélioré */}
      {chartData && (
        <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Box 
                sx={{ 
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}
              >
                <TimelineIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  Historique de Performance
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Évolution sur les 6 derniers mois
                </Typography>
              </Box>
            </Box>
            <Box height={300}>
              <Line data={chartData} options={chartOptions} />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Conseils et Points à Améliorer - Design amélioré */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Box 
              sx={{ 
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2
              }}
            >
              <SchoolIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                Points d'Attention & Conseils
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Axes d'amélioration pour progresser
              </Typography>
            </Box>
          </Box>
          
          <Grid container spacing={2}>
            {Object.entries(perf.details)
              .filter(([, detail]) => detail.score < 70)
              .map(([key, detail]) => {
                const conseils = {
                  presence: 'Veillez à arriver à l\'heure et à être présent régulièrement pour optimiser ce critère.',
                  delai_taches: 'Planifiez mieux votre temps et commencez vos tâches plus tôt pour respecter les deadlines.',
                  objectifs: 'Concentrez-vous sur vos objectifs prioritaires et suivez régulièrement votre progression.',
                  travail_equipe: 'Participez activement aux réunions et aidez vos collègues pour renforcer la collaboration.',
                  respect_regles: 'Assurez-vous de bien connaître et respecter le règlement intérieur et les procédures.',
                };

                const titres = {
                  presence: 'Présence & Ponctualité',
                  delai_taches: 'Délai d\'Exécution',
                  objectifs: 'Atteinte des Objectifs',
                  travail_equipe: 'Travail en Équipe',
                  respect_regles: 'Respect des Règles',
                };

                const icons = {
                  presence: '🎯',
                  delai_taches: '⏰',
                  objectifs: '🏆',
                  travail_equipe: '🤝',
                  respect_regles: '📋',
                };

                return (
                  <Grid item xs={12} key={key}>
                    <Alert 
                      severity="warning" 
                      icon={<span style={{ fontSize: '1.5rem' }}>{icons[key]}</span>}
                      sx={{ borderRadius: 2 }}
                    >
                      <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ color: '#e65100' }}>
                        {titres[key]} • {detail.score}%
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#5d4037' }}>
                        💡 {conseils[key]}
                      </Typography>
                    </Alert>
                  </Grid>
                );
              })}
            
            {Object.values(perf.details).every(detail => detail.score >= 70) && (
              <Grid item xs={12}>
                <Alert 
                  severity="success"
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: '#e8f5e9',
                    border: '1px solid #4caf50'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                    🎉 Excellent travail !
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1b5e20' }}>
                    Tous vos critères sont au-dessus de 70%. Vous êtes sur la bonne voie. Continuez sur cette lancée !
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Message de motivation final */}
      <Box 
        sx={{ 
          p: 3, 
          bgcolor: 'white', 
          borderRadius: 3, 
          border: '1px solid #e0e0e0',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e', mb: 1 }}>
          💪 Continuez à progresser !
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Votre performance est évaluée de manière transparente pour vous aider à identifier vos points forts 
          et vos axes d'amélioration. N'hésitez pas à échanger avec votre manager pour définir un plan d'action personnalisé.
        </Typography>
      </Box>
    </Box>
  );
};

export default MaPerformance;
